import { Children, useMemo, type ReactNode } from "react";
import { View, type ViewProps } from "react-native";
import { StyleSheet, UnistylesRuntime } from "react-native-unistyles";
import type { AppSpacing } from "@src/unistyles";

export type SimpleGridProps = ViewProps & {
  children: ReactNode;
  columns?: number | { xs?: number; sm?: number; md?: number; lg?: number; xl?: number };
  spacing?: AppSpacing;
  spacingX?: AppSpacing;
  spacingY?: AppSpacing;
};

export function SimpleGrid({
  children,
  columns = 1,
  spacing = "none",
  spacingX,
  spacingY,
  style,
  ...otherProps
}: SimpleGridProps) {
  const finalSpacingX = spacingX ?? spacing;
  const finalSpacingY = spacingY ?? spacing;

  const columnCount = useMemo(() => {
    if (typeof columns === "number") {
      return { xs: columns, sm: columns, md: columns, lg: columns, xl: columns };
    }
    return {
      xs: columns.xs ?? 1,
      sm: columns.sm ?? columns.xs ?? 1,
      md: columns.md ?? columns.sm ?? columns.xs ?? 1,
      lg: columns.lg ?? columns.md ?? columns.sm ?? columns.xs ?? 1,
      xl: columns.xl ?? columns.lg ?? columns.md ?? columns.sm ?? columns.xs ?? 1,
    };
  }, [columns]);

  const currentBreakpoint = UnistylesRuntime.breakpoint as keyof typeof columnCount;
  const currentColumns = columnCount[currentBreakpoint] || columnCount.xs;

  const childrenArray = Children.toArray(children);

  return (
    <View style={[styles(finalSpacingX, finalSpacingY, currentColumns).container, style]} {...otherProps}>
      {childrenArray.map((child, index) => (
        <View key={index} style={styles(finalSpacingX, finalSpacingY, currentColumns).item}>
          {child}
        </View>
      ))}
    </View>
  );
}

const styles = (spacingX: AppSpacing, spacingY: AppSpacing, columns: number) =>
  StyleSheet.create((theme) => ({
    container: {
      flexDirection: "row",
      flexWrap: "wrap",
      marginHorizontal: -theme.spacing[spacingX] / 2,
      marginVertical: -theme.spacing[spacingY] / 2,
    },
    item: {
      paddingHorizontal: theme.spacing[spacingX] / 2,
      paddingVertical: theme.spacing[spacingY] / 2,
      width: `${100 / columns}%`,
    },
  }));
